import axios from 'axios';
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { config } from '../../../../../config';
import UpdateSocialProof from "./UpdateProof";

const ViewProof = () => {
  const apiUrl = config.apiUrl;
  const imgUrl = config.imageUrl;
  const [socialProofs, setSocialProofs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    slug: '',
    image: null,
    name: '',
    designation: '',
    description: '',
  });

  const fetchSocialProofs = async () => {
    try {
      const response = await axios.get(`${apiUrl}/socialproofs`);
      setSocialProofs(response.data.data || response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching social proofs:', err);
      setError('Failed to load testimonials. Please try again later.');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSocialProofs();
  }, []);

  const handleEditClick = (socialProof) => {
    setEditId(socialProof.id);
    setFormData({
      slug: socialProof.slug,
      image: null,
      name: socialProof.name,
      designation: socialProof.designation, // Fixed typo here
      description: socialProof.description,
    });
  };

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const data = new FormData();
      data.append('_method', 'PUT');
      data.append('slug', formData.slug);
      data.append('name', formData.name);
      data.append('designation', formData.designation);
      data.append('description', formData.description);
      
      // Only append image if a new one was selected
      if (formData.image instanceof File) {
        data.append('image', formData.image);
      }

      const response = await axios.post(
        `${apiUrl}/socialproofsupdate/${editId}`, 
        data,
        {
          headers: { 
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setEditId(null);
      fetchSocialProofs();
      setError('');

    } catch (error) {
      console.error('Update error:', error.response?.data || error.message);
      setError(error.response?.data?.message || 'Failed to update testimonial');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this testimonial?')) {
      try {
        await axios.delete(`${apiUrl}/socialproofdelete/${id}`);
        setSocialProofs(prev => prev.filter(proof => proof.id !== id));
        setError('');
      } catch (err) {
        console.error('Delete error:', err);
        setError(err.response?.data?.message || 'Failed to delete testimonial');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-100 text-red-700 rounded-md max-w-2xl mx-auto">
        {error}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Testimonials</h1>
      </div>

      {socialProofs.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-500 text-lg">No testimonials found.</p>
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Image
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name/Slug
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Designation
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {socialProofs.map((proof) => (
                <tr key={proof.id}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {proof.image &&  (
                      <img 
                        src={`${imgUrl}/${proof.image}`} 
                        alt={proof.name} 
                        className="h-10 w-10 rounded-full object-cover"
                      />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{proof.name}</div>
                    <div className="text-sm text-gray-500">{proof.slug}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{proof.designation}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEditClick(proof)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(proof.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      
      {editId && 
        <div className="mt-8">
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center p-4 z-50 max-h-screen overflow-y-auto">
            <div className="p-6 mx-auto bg-white shadow rounded-lg min-w-[800px]">
              <UpdateSocialProof 
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                formData={formData}
                currentImage={socialProofs.find(p => p.id === editId)?.image_url}
                onCancel={() => setEditId(null)}
              /> 
            </div>
          </div>
        </div>
      }
    </div>
  );
};

export default ViewProof;